import { useNavigate, useParams } from 'react-router';
import { ScreenSkeleton } from '../../../../components/atoms/ScreenSkeleton';
import {
  ObservableLevelsEnum,
  PartCategory,
  PartName,
} from '../../../../data/enums';
import { CompleteButton } from '../../../../components/atoms/Button';
import { useEffect, useMemo, useState } from 'react';
import { ScreenHeading } from '../../../../components/molecules/ScreenHeading';
import { ActionStatusBar } from '../../../../components/molecules/ActionStatusBar';
import PhotoSelector from '../../../../components/atoms/PhotoSelector';
import { RichTextField } from '../../../../components/atoms/RichTextField';
import CheckboxWithLabel from '../../../../components/atoms/Checkbox';
import { Label } from '../../../../components/atoms/Label';
import { ScreenObservationLayout } from '../../../../components/templates/ScreenObservationLayout';
import { VisualCondition } from '../../../../components/atoms/RadioButtonGroupField';
import { ObservableItemIdentifier } from '../../../../api-contracts/other/ObservableItemIdentifier';
import { flattenObjectStrings } from '../../../../utils/flattenObjectString';
import { MdHelpers } from '../../../../helpers/mdHelpers';
import useInspectionCategoryType from '../../../../hooks/useInspectionCategoryType';
import { ErrorBanner } from '../../../../components/atoms/ErrorBanner';
import { useInspectionCached } from '../../../../hooks/useInspectionCached';
import { useAssetEquipmentPartCached } from '../../../../hooks/useAssetEquipmentPartCached';
import { ObservationDraftScope } from '../../../../components/ObservationDraftScope';

export const IdlerFrontScreen = () => {
  const { externalId, sideName } = useParams();
  const navigate = useNavigate();

  const { Undercarriage } = ObservableItemIdentifier.Identifiers;

  const { inspection } = useInspectionCached(externalId);

  const undercarriageCategory = useInspectionCategoryType({
    inspection,
  });

  const {
    equipmentPart,
    error: errorEquipmentPart,
    asset,
  } = useAssetEquipmentPartCached({
    externalId,
    asset: inspection?.asset,
    sideName,
    partCategory: PartCategory.idler,
    partName: PartName.frontIdler,
  });

  const [assetType, setAssetType] = useState<'ERS' | 'HEX'>();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    async function getEquipment() {
      try {
        if (asset) {
          setAssetType(asset.model.assetType as 'ERS' | 'HEX');
        }
      } catch (error) {
        console.error('Error fetching equipment', error);
        setError('Error fetching equipment');
      }
    }

    getEquipment();
  }, [asset, equipmentPart, errorEquipmentPart]);

  const listWearIndicators = useMemo(
    () =>
      [
        {
          label: 'Thrust Washer Damaged Or Missing',
          path: Undercarriage.Idler.WearIndicators.ThrustWasherDamagedOrMissing,
          type: ['ERS'],
        },
        {
          label: 'Leaking',
          path: Undercarriage.Idler.WearIndicators.Leaking,
          type: ['HEX'],
        },
        {
          label: 'Collapsed',
          path: Undercarriage.Idler.WearIndicators.Collapsed,
          type: ['ERS', 'HEX'],
        },
        {
          label: 'Excessive Guide Face Clearance',
          path: Undercarriage.Idler.WearIndicators.ExcessiveGuideFaceClearance,
          type: ['ERS', 'HEX'],
        },
        {
          label: 'Roller Path Cracking/Delamination/Plastic Deformation',
          path: Undercarriage.Idler.WearIndicators
            .RollerPathCrackingDelaminationPlasticDeformation,
          type: ['ERS', 'HEX'],
        },
        {
          path: Undercarriage.Idler.WearIndicators.SevereCracking,
          label: 'Severe Cracking',
          type: ['ERS', 'HEX'],
        },
      ]
        .filter((obj) => obj.type.includes(assetType || '--'))
        .map((el) => ({
          label: el.label,
          path: el.path,
        })),
    [assetType],
  );

  if (!equipmentPart || !externalId || !undercarriageCategory) {
    return <ScreenSkeleton fields={4} />;
  }

  if (error) {
    return <ErrorBanner message={error} />;
  }

  return (
    <ObservationDraftScope inspectionId={externalId}>
      <div className="-mt-4">
        <div className="sticky -top-4 z-10 bg-white pt-4">
          <ScreenHeading
            eyebrow="Idler"
            title="Front"
            tag={sideName?.split('-')[0]}
            partNumber={equipmentPart.part.partNumber}
            tooltipKey={'undercarriage-idler-front-screen-header'}
          />

          <div className="-mx-4 mt-2 bg-white">
            <ActionStatusBar
              inspectionId={externalId}
              inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
              dataLevel={ObservableLevelsEnum.equipmentPart}
              entities={[
                {
                  entityId: equipmentPart.id,
                  entityExternalId: equipmentPart.externalId,
                },
              ]}
              paths={[
                ...flattenObjectStrings(Undercarriage.Idler),
                // R8 fix: this screen previously recorded the FRONT condition under the REAR
                // identifier because no Front item existed (seeded 2026-08-23). Historical rows
                // stay under Rear (observations are never modified); new captures are Front.
                Undercarriage.Idler.Front.VisualCondition,
                ...listWearIndicators.map((e) => e.path),
              ]}
              pathsLike={[]}
              showPercentage={false}
            />
          </div>
        </div>

        <ScreenObservationLayout>
          <VisualCondition
            tooltipKey={'undercarriage-idler-front-visual-condition'}
            path={Undercarriage.Idler.Front.VisualCondition}
            inspectionId={externalId}
            inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
            dataLevel={ObservableLevelsEnum.equipmentPart}
            entityId={equipmentPart.id}
            entityExternalId={equipmentPart.externalId}
          />

          <Label
            titleType="fieldTitle"
            tooltipKey={'undercarriage-idler-front-wear-indicators'}
          >
            Wear Indicators
          </Label>
          {listWearIndicators.map((e) => (
            <CheckboxWithLabel
              key={e.path}
              inspectionId={externalId}
              inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
              dataLevel={ObservableLevelsEnum.equipmentPart}
              entityId={equipmentPart.id}
              entityExternalId={equipmentPart.externalId}
              infoTooltipKey={`undercarriage-idler-front-wearindicators-${MdHelpers.normaliseTooltipKeyComponent(e.label)}`}
              {...e}
            />
          ))}
          <PhotoSelector
            inspectionId={externalId}
            inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
            dataLevel={ObservableLevelsEnum.equipmentPart}
            title="Photos"
            entityId={equipmentPart.id}
            entityExternalId={equipmentPart.externalId}
            path={
              ObservableItemIdentifier.Identifiers.Undercarriage.Idler.Photo
            }
          />
          <RichTextField
            label="Comments"
            className="mt-4"
            path={
              ObservableItemIdentifier.Identifiers.Undercarriage.Idler.Comments
            }
            inspectionId={externalId}
            inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
            dataLevel={ObservableLevelsEnum.equipmentPart}
            entityId={equipmentPart.id}
            entityExternalId={equipmentPart.externalId}
          />

          <CompleteButton onClick={() => navigate(-1)} />
        </ScreenObservationLayout>
      </div>
    </ObservationDraftScope>
  );
};
